
import {
  defineComponent, ref, inject, computed, ComputedRef,
} from 'vue';
import { useI18n } from 'vue-i18n';
import PageHeader from '@/components/layout/PageHeader.vue';
import Tooltip from 'primevue/tooltip';
import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import SpmDateDisplay from '@/components/table-rest-api/SpmDateDisplay.vue';
import SpmBadge from '@/components/table-rest-api/SpmBadge.vue';
import Flag from '@/components/table/renderers/Flag.vue';
import { NestApiType } from '@/composables/nestApi';
import type {
  SpmColumnConfig,
  ApiCallConfig,
  SpmApiFilter,
  ExportPayload,
} from '@/components/table-rest-api/types';
import { SpmFilterOperator } from '@/components/table-rest-api/types';
import { showToastError } from '@/helpers';

export default defineComponent({
  name: 'CustomersV2',
  components: {
    PageHeader,
    SpmTableAPI,
    SpmDateDisplay,
    SpmBadge,
    Flag,
  },

  directives: {
    tooltip: Tooltip,
  },

  setup() {
    const { t } = useI18n();

    // Base filters that are always applied (not source-related)
    const baseFilters: SpmApiFilter[] = [
      {
        field: 'deleted',
        operator: SpmFilterOperator.EQUALS,
        values: 0,
      },
    ];

    const globalSourceFilters = inject<ComputedRef<SpmApiFilter[]>>('globalSourceFilters', computed(() => []));
    const persistentFilters = computed(() => [...baseFilters, ...globalSourceFilters.value]);

    // API call configuration
    const apiCall = ref<ApiCallConfig | null>({
      type_api: NestApiType.CLIENT_API,
      path: '/customers',
      secure: true,
    });

    const columns: SpmColumnConfig[] = [
      {
        id: 'id',
        header: '',
        fields: ['id'],
        hidden: true,
      },
      {
        id: 'customer_id',
        header: '',
        fields: [{
          name: 'customer_id',
          label: t('myShopData.customers.headers.id_customer_shop'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],

        hidden: true,
      },
      {
        id: 'fullname',
        header: t('myShopData.customers.headers.fullName'),
        fields: [{
          name: 'first_name',
          label: t('myShopData.customers.headers.firstname'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }, {
          name: 'last_name',
          label: t('myShopData.customers.headers.lastname'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }, {
          name: 'created_at',
          label: t('myShopData.customers.headers.creationDate'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }, {
          name: 'updated_at',
          label: t('myShopData.customers.headers.modifDate'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }],
      },
      {
        id: 'email',
        header: t('myShopData.customers.headers.email'),
        fields: [{
          name: 'email',
          label: t('myShopData.customers.headers.email'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'gender',
        header: t('myShopData.customers.headers.gender'),
        fields: [{
          name: 'gender',
          label: t('myShopData.customers.headers.gender'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'multiSelect',
            options: [
              { label: t('gender.unknown'), value: '0' },
              { label: t('gender.male'), value: '1' },
              { label: t('gender.female'), value: '2' },
            ],
          },
        }],
      },
      {
        id: 'birthday',
        header: t('myShopData.customers.headers.birthday'),
        fields: [{
          name: 'birth_date',
          label: t('myShopData.customers.headers.birthday'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }],
      },
      {
        id: 'newsletter',
        header: t('myShopData.customers.headers.newsletter'),
        fields: [{
          name: 'is_newsletter_subscribed',
          label: t('myShopData.customers.headers.newsletter'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'multiSelect',
            options: [
              { label: t('myLists.manage.customers.optin.0'), value: '0' },
              { label: t('myLists.manage.customers.optin.1'), value: '1' },
              { label: t('myLists.manage.customers.optin.2'), value: '2' },
            ],
          },
        }],
      },
      // Composite: location with country and lang
      {
        id: 'location',
        header: t('myShopData.customers.headers.location'),
        fields: [{
          name: 'country',
          label: t('myShopData.customers.headers.country'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'countrySelect',
          },
        }, {
          name: 'lang',
          label: t('myShopData.customers.headers.lang'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'active',
        header: t('myShopData.customers.headers.active'),
        fields: [{
          name: 'is_active',
          label: t('myShopData.customers.headers.active'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'multiSelect',
            options: [
              { label: t('active'), value: '1' },
              { label: t('inactive'), value: '0' },
            ],
          },
        }],
      },
    ];

    /**
     * Handle export action from SpmTableAPI
     */
    const handleExport = async (payload: ExportPayload) => {
      try {
        // Build export request payload - already simplified from SpmTableAPI
        const exportRequest: Record<string, unknown> = {
          entity_type: payload.exportConfig.entityType,
          select_all_mode: payload.selectAllMode,
          selected_ids: payload.selectedIds,
          total_count: payload.totalCount,
          ...payload.exportConfig.additionalParams,
        };

        // Add filters only if present (selectAllMode from selection, not "Export all" button)
        if (payload.filters) {
          exportRequest.filters = payload.filters;
        }

        console.log('Export request:', exportRequest);
        // TODO : Call export API
        // const response = await nestCall(
        //   'post',
        //   NestApiType.CLIENT_API,
        //   '/exports/contacts',
        //   exportRequest,
        //   '',
        //   undefined,
        //   true,
        // );

        // if (response && !response.error) {
        //   await showToastSuccess(t('myLists.manage.customers.export.success'));
        // } else {
        //   await showToastError(t('myLists.manage.customers.export.error'));
        // }
      } catch (error) {
        console.error('Export error:', error);
        await showToastError(t('myLists.manage.customers.export.error'));
      }
    };

    // Helper to clean null/undefined strings
    const cleanStr = (str: string | null | undefined): string => {
      if (!str || str === 'null' || str === 'undefined') return '';
      return str;
    };

    // Helper to format full name
    const formatFullName = (firstName: string | null | undefined, lastName: string | null | undefined): string => {
      const first = cleanStr(firstName);
      const last = cleanStr(lastName);
      if (!first && !last) return '';
      return `${first} ${last}`.trim();
    };

    return {
      t,
      columns,
      apiCall,
      persistentFilters,
      handleExport,
      formatFullName,
      cleanStr,
    };
  },
});
